import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import StarIcon from "@mui/icons-material/Star";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { api, T, inputSx, fmtCAD, PRICING_LABELS } from "./api";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { API_URL } from "../../../config";
import CategoriesSection from "./CategoriesSection";
import SubcategoriesSection from "./SubcategoriesSection";
import ItemProfileDrawer from "../ItemsManager/ItemProfileDrawer";

const buildImageUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}/../${path}`;
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEMS SECTION (within service profile)
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemsSection: React.FC<{
  serviceId: number;
  categories: any[];
  serviceColor: string;
  onRefresh: () => void;
}> = ({ serviceId, categories, serviceColor, onRefresh }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [profileItemId, setProfileItemId] = useState<number | null>(null);

  // Add/Edit item modal
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form fields
  const [itemName, setItemName] = useState("");
  const [itemCategoryId, setItemCategoryId] = useState("");
  const [itemSubcategoryId, setItemSubcategoryId] = useState("");
  const [itemUnit, setItemUnit] = useState("");
  const [itemDefaultPrice, setItemDefaultPrice] = useState("");
  const [itemSourceType, setItemSourceType] = useState("inhouse");
  const [itemStatus, setItemStatus] = useState("active");
  const [itemDescription, setItemDescription] = useState("");
  const [itemImageUrl, setItemImageUrl] = useState<string | null>(null);
  const [itemUploading, setItemUploading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const res = await api.get(`/banquet/items-list?service_id=${serviceId}`);
    if (res.code === 200) setItems(res.data ?? []);
    setLoading(false);
  }, [serviceId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = useMemo(() =>
    items.filter((i) => !search || i.name.toLowerCase().includes(search.toLowerCase())),
    [items, search]);

  const subcategories = useMemo(() => {
    if (!itemCategoryId) return [];
    const cat = categories.find((c: any) => c.id === Number(itemCategoryId));
    return cat?.subcategories ?? [];
  }, [categories, itemCategoryId]);

  const openForm = (item: any | null) => {
    setEditItem(item);
    setItemName(item?.name || "");
    setItemCategoryId(item?.category_id?.toString() || "");
    setItemSubcategoryId(item?.subcategory_id?.toString() || "");
    setItemUnit(item?.unit || "");
    setItemDefaultPrice(item?.default_price?.toString() || "");
    setItemSourceType(item?.source_type || "inhouse");
    setItemStatus(item?.status || "active");
    setItemDescription(item?.description || "");
    setItemImageUrl(item?.image_url || null);
    setFormOpen(true);
  };

  const handleItemImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/") || file.size > 2 * 1024 * 1024) return;
    setItemUploading(true);
    try { const path = await uploadImage(file); if (path) setItemImageUrl(path); } catch {}
    setItemUploading(false);
  };

  const handleSave = async () => {
    if (!itemName.trim()) return;
    setSaving(true);
    const res = await api.post("/banquet/service-tree/item", {
      id: editItem?.id,
      service_id: serviceId,
      category_id: itemCategoryId ? Number(itemCategoryId) : null,
      subcategory_id: itemSubcategoryId ? Number(itemSubcategoryId) : null,
      name: itemName.trim(),
      unit: itemUnit || null,
      default_price: itemDefaultPrice ? Number(itemDefaultPrice) : null,
      source_type: itemSourceType,
      status: itemStatus,
      description: itemDescription || null,
      image_url: itemImageUrl || null,
    });
    if (res.code === 200) {
      showMessage(editItem?.id ? "Item updated" : "Item created");
      setFormOpen(false);
      setEditItem(null);
      fetchItems();
      onRefresh();
    } else showErrorMessage(res.message || "Failed to save");
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const res = await api.del(`/banquet/service-tree/item/${deleteTarget.id}`);
    if (res.code === 200) { showMessage("Item deleted"); fetchItems(); onRefresh(); }
    else showErrorMessage(res.message);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  const thSx = { fontWeight: 700, fontSize: 11, color: T.slate, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5 } as const;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InventoryOutlinedIcon sx={{ fontSize: 18, color: T.greenTx }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.navy }}>Items</Typography>
          <Chip label={items.length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: T.greenBg, color: T.greenTx }} />
        </Box>
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => openForm(null)}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: T.gold, bgcolor: `${T.gold}10`, borderRadius: "8px", px: 1.5, "&:hover": { bgcolor: `${T.gold}20` } }}>
          Add Item
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 1.5 }}>
        <TextField size="small" placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, maxWidth: 300 }} />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: T.gold }} /></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: "center", borderRadius: "12px", border: `1px dashed ${T.line}` }}>
          <InventoryOutlinedIcon sx={{ fontSize: 32, color: T.slateLt, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: T.slateLt }}>
            {items.length === 0 ? "No items yet. Add items to this service." : "No items match your search."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${T.line}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: T.bg }}>
                <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
                <TableCell sx={thSx}>Item</TableCell>
                <TableCell sx={thSx}>Category</TableCell>
                <TableCell sx={thSx}>Subcategory</TableCell>
                <TableCell sx={thSx}>Default Vendor</TableCell>
                <TableCell align="right" sx={thSx}>Price</TableCell>
                <TableCell align="center" sx={thSx}>Vendors</TableCell>
                <TableCell align="center" sx={thSx}>Status</TableCell>
                <TableCell align="right" sx={thSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((item: any, idx: number) => {
                const defVP = item.default_vendor_price;
                return (
                  <TableRow key={item.id} hover sx={{ cursor: "pointer", "&:hover": { bgcolor: "#fef6ee" } }}
                    onClick={() => setProfileItemId(item.id)}>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slateLt }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                        {item.image_url ? (
                          <Box component="img" src={buildImageUrl(item.image_url) || ""} alt="" sx={{
                            width: 36, height: 36, borderRadius: "8px", objectFit: "cover",
                            border: `1px solid ${T.line}`, flexShrink: 0,
                          }} />
                        ) : (
                          <Box sx={{
                            width: 36, height: 36, borderRadius: "8px", bgcolor: T.bg,
                            border: `1px solid ${T.line}`, display: "flex", alignItems: "center",
                            justifyContent: "center", flexShrink: 0,
                          }}>
                            <ImageOutlinedIcon sx={{ fontSize: 16, color: T.slateLt }} />
                          </Box>
                        )}
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{item.name}</Typography>
                          {item.unit && <Typography sx={{ fontSize: 10, color: T.slateLt }}>{item.unit}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slate }}>{item.category?.name ?? "—"}</TableCell>
                    <TableCell sx={{ py: 1.2, fontSize: 12, color: T.slate }}>{item.subcategory?.name ?? "—"}</TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      {defVP ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 11, color: T.gold }} />
                          <Typography sx={{ fontSize: 12, color: T.navy }}>{defVP.vendor?.name ?? "—"}</Typography>
                        </Box>
                      ) : <Typography sx={{ fontSize: 12, color: T.slateLt }}>—</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.2 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: item.default_price != null ? T.greenTx : T.slateLt }}>
                        {item.default_price != null ? fmtCAD(Number(item.default_price)) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.2 }}>
                      <Chip label={(item.vendor_prices ?? []).length} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: `${T.gold}12`, color: T.gold }} />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.2 }}>
                      <Chip label={item.status} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: item.status === "active" ? T.greenBg : T.redBg, color: item.status === "active" ? T.greenTx : T.redTx }} />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.2 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openForm(item)} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget({ id: item.id, name: item.name })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Item Modal */}
      <Dialog open={formOpen} onClose={() => { setFormOpen(false); setEditItem(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
        <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <InventoryOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>{editItem?.id ? "Edit Item" : "New Item"}</Typography>
          </Box>
          <IconButton onClick={() => { setFormOpen(false); setEditItem(null); }} sx={{ color: "rgba(255,255,255,0.6)" }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Item Name *" size="small" value={itemName} onChange={(e) => setItemName(e.target.value)} autoFocus fullWidth sx={inputSx} />

          {/* Image Upload */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {itemImageUrl ? (
              <Box sx={{ position: "relative", width: 80, height: 60, borderRadius: "10px", overflow: "hidden", border: `1px solid ${T.line}` }}>
                <Box component="img" src={buildImageUrl(itemImageUrl) || ""} alt="Item" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <IconButton size="small" onClick={() => setItemImageUrl(null)} sx={{
                  position: "absolute", top: 1, right: 1, bgcolor: "rgba(0,0,0,0.5)", width: 18, height: 18,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}>
                  <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{
                width: 80, height: 60, borderRadius: "10px", border: `1.5px dashed ${T.line}`,
                display: "flex", alignItems: "center", justifyContent: "center", bgcolor: T.bg,
              }}>
                <ImageOutlinedIcon sx={{ fontSize: 22, color: T.slateLt }} />
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Button component="label" size="small" variant="outlined" disabled={itemUploading}
                startIcon={itemUploading ? <CircularProgress size={12} /> : <CloudUploadOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: T.line, color: T.slate, borderRadius: "8px" }}>
                {itemUploading ? "Uploading..." : itemImageUrl ? "Change Image" : "Upload Image"}
                <input type="file" accept="image/*" hidden onChange={handleItemImageUpload} />
              </Button>
              <Typography sx={{ fontSize: 10, color: T.slateLt, mt: 0.3 }}>JPG, PNG, WebP. Max 2MB.</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField select label="Category" size="small" value={itemCategoryId}
              onChange={(e) => { setItemCategoryId(e.target.value); setItemSubcategoryId(""); }} sx={inputSx}>
              <MenuItem value="">None</MenuItem>
              {categories.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select label="Subcategory" size="small" value={itemSubcategoryId}
              onChange={(e) => setItemSubcategoryId(e.target.value)} sx={inputSx} disabled={subcategories.length === 0}>
              <MenuItem value="">None</MenuItem>
              {subcategories.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField label="Unit" size="small" placeholder="e.g. per_plate, per_event" value={itemUnit}
              onChange={(e) => setItemUnit(e.target.value)} sx={inputSx} />
            <TextField label="Default Price" size="small" type="number" value={itemDefaultPrice}
              onChange={(e) => setItemDefaultPrice(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField select label="Source Type" size="small" value={itemSourceType} onChange={(e) => setItemSourceType(e.target.value)} sx={inputSx}>
              <MenuItem value="inhouse">In-House</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </TextField>
            <TextField select label="Status" size="small" value={itemStatus} onChange={(e) => setItemStatus(e.target.value)} sx={inputSx}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
          <TextField label="Description" size="small" value={itemDescription} onChange={(e) => setItemDescription(e.target.value)}
            fullWidth multiline rows={2} sx={inputSx} />
        </DialogContent>

        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
          <Button onClick={() => { setFormOpen(false); setEditItem(null); }} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !itemName.trim()}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
            {saving ? "Saving..." : "Save Item"}
          </Button>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Delete item?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>Delete <strong>{deleteTarget?.name}</strong>?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{isDeleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>

      {/* Item Profile Drawer */}
      <ItemProfileDrawer itemId={profileItemId} onClose={() => setProfileItemId(null)} onRefresh={() => { fetchItems(); onRefresh(); }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN SERVICE PROFILE PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const ServiceProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [svc, setSvc] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchService = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await api.get(`/banquet/services-list/${id}`);
    if (res.code === 200) setSvc(res.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchService(); }, [fetchService]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>;
  }

  if (!svc) {
    return (
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
        <Typography sx={{ py: 10, textAlign: "center", color: T.slateLt }}>Service not found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* ── Breadcrumb ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, pt: 1 }}>
        <IconButton size="small" onClick={() => navigate("/banquet/services-manager")} sx={{ color: T.slateLt }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ fontSize: 12, color: T.slateLt, cursor: "pointer", "&:hover": { color: T.gold } }}
          onClick={() => navigate("/banquet/services-manager")}>Services</Typography>
        <Typography sx={{ fontSize: 12, color: T.slateLt }}>/</Typography>
        <Typography sx={{ fontSize: 12, color: T.navy, fontWeight: 600 }}>{svc.name}</Typography>
      </Box>

      {/* ── Service Header Card ── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "14px", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "14px", bgcolor: `${svc.color ?? T.slateLt}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: svc.color ?? T.slateLt }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.navy }}>{svc.name}</Typography>
            <Chip
              label={svc.service_status ?? svc.status ?? "active"} size="small"
              sx={{
                height: 22, fontSize: 10, fontWeight: 600,
                bgcolor: (svc.service_status ?? svc.status ?? "active") === "active" ? T.greenBg : T.redBg,
                color: (svc.service_status ?? svc.status ?? "active") === "active" ? T.greenTx : T.redTx,
              }}
            />
          </Box>
          {svc.description && <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>{svc.description}</Typography>}
        </Box>
      </Paper>

      {/* ── Stats ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 3 }}>
        {[
          { label: "Categories", value: svc.categories_count, icon: <CategoryOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />, bg: "#3b82f612" },
          { label: "Subcategories", value: svc.subcategories_count, icon: <FolderOpenOutlinedIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />, bg: "#8b5cf612" },
          { label: "Items", value: svc.items_count, icon: <InventoryOutlinedIcon sx={{ fontSize: 18, color: T.greenTx }} />, bg: T.greenBg },
          { label: "Vendors", value: svc.vendors_count, icon: <GroupOutlinedIcon sx={{ fontSize: 18, color: T.gold }} />, bg: `${T.gold}12` },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${T.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: stat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{stat.icon}</Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.navy, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt }}>{stat.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Categories Section ── */}
      <CategoriesSection
        serviceId={Number(id)}
        categories={svc.categories ?? []}
        serviceColor={svc.color ?? T.slateLt}
        onRefresh={fetchService}
      />

      {/* ── Subcategories Section ── */}
      <SubcategoriesSection
        serviceId={Number(id)}
        categories={svc.categories ?? []}
        serviceColor={svc.color ?? T.slateLt}
        onRefresh={fetchService}
      />

      {/* ── Items Section ── */}
      <ItemsSection
        serviceId={Number(id)}
        categories={svc.categories ?? []}
        serviceColor={svc.color ?? T.slateLt}
        onRefresh={fetchService}
      />
    </Container>
  );
};

export default ServiceProfilePage;
